// Single source of truth for the IO Output function picker. Each preset writes
// the firmware-shipped fields ext_gpi_idle_state / motion_during / motion_pulse_start /
// motion_pulse_stop / motion_period_ms / motion_pulse_width_ms in a known combination.
// Every non-advanced preset also forces gpo_output to 0 so that opening a legacy
// template (which only carried the old gpo_output flag) and saving via a preset
// cleanly clears that flag. Without this the API mutual-exclusion validator
// rejects the combination of gpo_output=1 and any of the new motion fields.
// Keep this list in sync with the mobile app's io_function_presets.dart.
// Bump this version whenever the preset list or field mappings change in either client.
export const IO_PRESETS_SCHEMA_VERSION = 2;

export const MOTION_DURING = Object.freeze({
    NONE: 0,
    SUSTAIN: 1,
    PULSE: 2,
});

export const ACTIVE_LEVEL = Object.freeze({
    HIGH: 0, // legacy default
    LOW: 1,
});

export const NOTIFY_EDGE = Object.freeze({
    ANY: 0, // legacy default
    RISING: 1,
    FALLING: 2,
});

// Edge-pulse width presets, in milliseconds.
export const EDGE_PULSE_WIDTHS = Object.freeze([
    { key: 'short', label: 'Short (100 ms)', ms: 100 },
    { key: 'medium', label: 'Medium (200 ms)', ms: 200 },
    { key: 'long', label: 'Long (500 ms)', ms: 500 },
]);

// Beacon (pulse-train) patterns.
export const BEACON_PATTERNS = Object.freeze([
    { key: 'slow', label: 'Slow blink (1 Hz)', period: 1000, width: 500 },
    { key: 'medium', label: 'Medium blink (2 Hz)', period: 500, width: 250 },
    { key: 'fast', label: 'Fast blink (4 Hz)', period: 250, width: 50 },
    { key: 'strobe', label: 'Strobe', period: 200, width: 20 },
]);

// Each function preset returns the exact firmware-bound fields. Fields not relevant to
// the preset are written as their zero default so the firmware behaviour is deterministic.
export const OUTPUT_FUNCTIONS = Object.freeze([
    {
        key: 'off',
        label: 'Off',
        description: 'Output line stays inactive while the device is powered.',
        fields: () => ({
            gpo_output: 0,
            ext_gpi_idle_state: 0,
            ext_gpi_motion_during: MOTION_DURING.NONE,
            ext_gpi_motion_pulse_start: 0,
            ext_gpi_motion_pulse_stop: 0,
            ext_gpi_motion_period_ms: 0,
            ext_gpi_motion_pulse_width_ms: 0,
        }),
    },
    {
        key: 'status_indicator',
        label: 'Status indicator',
        description: 'Stay on the whole time the device is powered.',
        fields: () => ({
            gpo_output: 0,
            ext_gpi_idle_state: 1,
            ext_gpi_motion_during: MOTION_DURING.NONE,
            ext_gpi_motion_pulse_start: 0,
            ext_gpi_motion_pulse_stop: 0,
            ext_gpi_motion_period_ms: 0,
            ext_gpi_motion_pulse_width_ms: 0,
        }),
    },
    {
        key: 'active_while_moving',
        label: 'Active while moving',
        description: 'Drive the output the whole time the vehicle is moving.',
        fields: () => ({
            gpo_output: 0,
            ext_gpi_idle_state: 0,
            ext_gpi_motion_during: MOTION_DURING.SUSTAIN,
            ext_gpi_motion_pulse_start: 0,
            ext_gpi_motion_pulse_stop: 0,
            ext_gpi_motion_period_ms: 0,
            ext_gpi_motion_pulse_width_ms: 0,
        }),
    },
    {
        key: 'beacon_while_moving',
        label: 'Beacon while moving',
        description: 'Pulse on a repeating pattern while the vehicle is moving.',
        fields: (opts = {}) => {
            const pattern = BEACON_PATTERNS.find(p => p.key === opts.beacon) || BEACON_PATTERNS[0];
            return {
                gpo_output: 0,
                ext_gpi_idle_state: 0,
                ext_gpi_motion_during: MOTION_DURING.PULSE,
                ext_gpi_motion_pulse_start: 0,
                ext_gpi_motion_pulse_stop: 0,
                ext_gpi_motion_period_ms: pattern.period,
                ext_gpi_motion_pulse_width_ms: pattern.width,
            };
        },
    },
    {
        key: 'pulse_on_start',
        label: 'Pulse on trip start',
        description: 'Brief pulse at the moment the vehicle starts moving.',
        fields: (opts = {}) => ({
            gpo_output: 0,
            ext_gpi_idle_state: 0,
            ext_gpi_motion_during: MOTION_DURING.NONE,
            ext_gpi_motion_pulse_start: 1,
            ext_gpi_motion_pulse_stop: 0,
            ext_gpi_motion_period_ms: 0,
            ext_gpi_motion_pulse_width_ms: edgeWidthMs(opts.edge),
        }),
    },
    {
        key: 'pulse_on_end',
        label: 'Pulse on trip end',
        description: 'Brief pulse at the moment the vehicle stops.',
        fields: (opts = {}) => ({
            gpo_output: 0,
            ext_gpi_idle_state: 0,
            ext_gpi_motion_during: MOTION_DURING.NONE,
            ext_gpi_motion_pulse_start: 0,
            ext_gpi_motion_pulse_stop: 1,
            ext_gpi_motion_period_ms: 0,
            ext_gpi_motion_pulse_width_ms: edgeWidthMs(opts.edge),
        }),
    },
    {
        key: 'pulse_on_both',
        label: 'Pulse on trip start and end',
        description: 'Brief pulse on both edges of a trip.',
        fields: (opts = {}) => ({
            gpo_output: 0,
            ext_gpi_idle_state: 0,
            ext_gpi_motion_during: MOTION_DURING.NONE,
            ext_gpi_motion_pulse_start: 1,
            ext_gpi_motion_pulse_stop: 1,
            ext_gpi_motion_period_ms: 0,
            ext_gpi_motion_pulse_width_ms: edgeWidthMs(opts.edge),
        }),
    },
    {
        key: 'advanced',
        label: 'Advanced…',
        description: 'Set each motion field manually.',
        fields: () => null, // signals the form to expose raw fields
    },
]);

function edgeWidthMs(key) {
    const match = EDGE_PULSE_WIDTHS.find(p => p.key === key);
    return match ? match.ms : EDGE_PULSE_WIDTHS[1].ms; // default Medium
}

// Reverse mapping: given a fields object, work out which preset it matches.
// Returns { functionKey, beaconKey?, edgeKey? }. Falls back to 'advanced' when nothing fits.
export function fieldsToPreset(fields) {
    const f = {
        idle: numOrZero(fields.ext_gpi_idle_state),
        during: numOrZero(fields.ext_gpi_motion_during),
        ps: numOrZero(fields.ext_gpi_motion_pulse_start),
        pe: numOrZero(fields.ext_gpi_motion_pulse_stop),
        period: numOrZero(fields.ext_gpi_motion_period_ms),
        width: numOrZero(fields.ext_gpi_motion_pulse_width_ms),
    };

    // Legacy: the old single-checkbox "HIGH during movement" maps to active-while-moving.
    // Only consult the legacy field if no new motion field is set.
    const anyNew = f.idle || f.during || f.ps || f.pe || f.period || f.width;
    if (!anyNew && fields.gpo_output) {
        return { functionKey: 'active_while_moving', isLegacyMigration: true };
    }

    if (f.idle === 1 && f.during === 0 && !f.ps && !f.pe) {
        return { functionKey: 'status_indicator' };
    }
    if (f.during === MOTION_DURING.SUSTAIN && !f.ps && !f.pe) {
        return { functionKey: 'active_while_moving' };
    }
    if (f.during === MOTION_DURING.PULSE && !f.ps && !f.pe) {
        const beacon = BEACON_PATTERNS.find(p => p.period === f.period && p.width === f.width);
        if (beacon) return { functionKey: 'beacon_while_moving', beaconKey: beacon.key };
        return { functionKey: 'advanced' };
    }
    if (f.during === 0 && f.ps && !f.pe) {
        const edge = EDGE_PULSE_WIDTHS.find(p => p.ms === f.width);
        return { functionKey: 'pulse_on_start', edgeKey: edge ? edge.key : null };
    }
    if (f.during === 0 && !f.ps && f.pe) {
        const edge = EDGE_PULSE_WIDTHS.find(p => p.ms === f.width);
        return { functionKey: 'pulse_on_end', edgeKey: edge ? edge.key : null };
    }
    if (f.during === 0 && f.ps && f.pe) {
        const edge = EDGE_PULSE_WIDTHS.find(p => p.ms === f.width);
        return { functionKey: 'pulse_on_both', edgeKey: edge ? edge.key : null };
    }
    if (!anyNew) {
        return { functionKey: 'off' };
    }
    return { functionKey: 'advanced' };
}

function numOrZero(v) {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

// One-line plain-English summary of what the line will do, shown under the picker.
export function previewText(functionKey, opts = {}) {
    switch (functionKey) {
        case 'off':
            return 'Output stays off.';
        case 'status_indicator':
            return 'Output is on the whole time the device is powered.';
        case 'active_while_moving':
            return 'Output is on whenever the vehicle is moving.';
        case 'beacon_while_moving': {
            const p = BEACON_PATTERNS.find(x => x.key === opts.beacon) || BEACON_PATTERNS[0];
            return `Output pulses ${p.width} ms on, ${p.period - p.width} ms off, while the vehicle is moving.`;
        }
        case 'pulse_on_start':
            return `Output pulses for ${edgeWidthMs(opts.edge)} ms at the moment the vehicle starts moving.`;
        case 'pulse_on_end':
            return `Output pulses for ${edgeWidthMs(opts.edge)} ms at the moment the vehicle stops.`;
        case 'pulse_on_both':
            return `Output pulses for ${edgeWidthMs(opts.edge)} ms at both the start and the end of every trip.`;
        case 'advanced':
            return 'Each motion field is set manually below.';
        default:
            return '';
    }
}
